// The map's style: CARTO's Dark Matter vector basemap, tinted to the page's
// navy, plus the network as GeoJSON layers. Line and station sizes follow
// render.ts (railGap, stationRadius, stationLine) and the Leaflet page's line
// weights, converted from Leaflet zooms to MapLibre zooms (one less; see
// view.ts) and written as zoom expressions, so MapLibre evaluates them every
// frame on the GPU.

import type {
  ExpressionSpecification,
  LayerSpecification,
  Map as MapLibreMap,
  GeoJSONSourceSpecification,
  RequestParameters,
  StyleSpecification,
} from 'maplibre-gl';
import type * as Core from './core.ts';
import type { LineLayout, LineId, Network, Station, StationId, PageLine } from './types.ts';
import {
  CASING,
  INK,
  SPLIT_Z,
  SPLIT_FROM as LEAFLET_SPLIT_FROM,
  SPLIT_FULL as LEAFLET_SPLIT_FULL,
  isDark,
  isInterchange,
} from './render.ts';
import { MINOR, isMinor, type MinorLines } from './minor.ts';
import { ZOOM_OFFSET } from './view.ts';
import { visiblePaths } from './visible-paths.ts';
import type { Box } from './onscreen.ts';
import { simplify } from './osm-track.ts';
import { cooperate, finish } from './work.ts';
import type { LatLngTuple } from './types.ts';
import { COUNTRY_BORDERS, COUNTRY_BORDERS_110M, COUNTRY_BORDERS_50M } from './country-borders.ts';
import DARK_MATTER from './dark-matter.json' with { type: 'json' };

// CARTO's Dark Matter in the OpenMapTiles schema (source "carto"), which
// OVERRIDES and ADDED style, vendored from
// https://raw.githubusercontent.com/CartoDB/basemap-styles/master/mapboxgl/dark-matter.json
// and bundled with the page. CARTO's unversioned style URL moved to another
// schema in October 2026, and no layer here matched it.
export const CARTO_STYLE: unknown = DARK_MATTER;
export const COUNTRY_BORDER_DETAIL_ZOOM = 7;

// Leaflet zoom -> MapLibre zoom.
const ml = (leafletZoom: number): number => leafletZoom - ZOOM_OFFSET;
// The zoom where lines split into two tracks, in MapLibre zooms.
const SPLIT_ZOOM = ml(SPLIT_Z);
const SPLIT_FROM = ml(LEAFLET_SPLIT_FROM);
const SPLIT_FULL = ml(LEAFLET_SPLIT_FULL);

// CARTO's terms: the key goes on every request, including style, tiles,
// glyphs and sprites. The style's tile URLs (from its TileJSON) carry no key,
// so add it here rather than rewriting the style.
export function cartoTransform(key: string): (url: string) => RequestParameters {
  return (url) => {
    let u: URL;
    try {
      u = new URL(url);
    } catch {
      return { url };
    }
    if (u.hostname !== 'cartocdn.com' && !u.hostname.endsWith('.cartocdn.com')) return { url };
    if (!u.searchParams.has('key')) u.searchParams.set('key', key);
    return { url: u.href };
  };
}

// Without a CARTO key: a plain ink background, and the network alone
// outlines London. There are no glyphs then, so no station names.
export const EMPTY_STYLE: StyleSpecification = {
  version: 8,
  sources: {},
  layers: [{ id: 'background', type: 'background', paint: { 'background-color': INK } }],
};

// ---- the basemap's tint

// The Leaflet page drew CARTO's raster tiles at 60% opacity with CSS
// saturate(0.4), over the ink background. Both are linear in each color
// channel, and so is alpha blending, so applying them to every color in the
// style gives the same picture: opaque layers exactly, translucent ones too.
const TILE_OPACITY = 0.6;
const SATURATE = 0.4;
const INK_RGB = [0x0a, 0x12, 0x30] as const;

type Rgba = readonly [r: number, g: number, b: number, a: number];

function hslToRgb(h: number, s: number, l: number): readonly [number, number, number] {
  const k = (n: number): number => (n + h / 30) % 12;
  const a = s * Math.min(l, 1 - l);
  const f = (n: number): number => (l - a * Math.max(-1, Math.min(k(n) - 3, 9 - k(n), 1))) * 255;
  return [f(0), f(8), f(4)];
}

// A CSS color component: a number, or a percentage of percentOf.
function cssNumber(p: string | undefined, percentOf: number): number {
  if (p === undefined || p === '') return Number.NaN;
  return p.endsWith('%') ? (Number(p.slice(0, -1)) / 100) * percentOf : Number(p);
}

// The CSS colors CARTO's styles use: #rgb, #rrggbb, rgb(), rgba(), hsl(),
// hsla(). Anything else is null and left alone.
export function parseColor(text: string): Rgba | null {
  const t = text.trim().toLowerCase();
  const hex = /^#([0-9a-f]{3}|[0-9a-f]{6})$/u.exec(t)?.[1];
  if (hex !== undefined) {
    const full = hex.length === 3 ? hex.replaceAll(/./gu, '$&$&') : hex;
    const n = parseInt(full, 16);
    return [(n >> 16) & 255, (n >> 8) & 255, n & 255, 1];
  }
  const fn = /^(rgba?|hsla?)\(([^)]*)\)$/u.exec(t);
  if (!fn) return null;
  const kind = fn[1] ?? '';
  const parts = (fn[2] ?? '').split(/[\s,/]+/u).filter((p) => p !== '');
  if (parts.length < 3 || parts.length > 4) return null;
  const num = cssNumber;
  const alpha = parts[3] === undefined ? 1 : num(parts[3], 1);
  const rgb: readonly number[] = kind.startsWith('rgb')
    ? parts.slice(0, 3).map((p) => num(p, 255))
    : hslToRgb(num(parts[0], 360), num(parts[1], 1), num(parts[2], 1));
  const [r, g, b] = rgb;
  if (r === undefined || g === undefined || b === undefined) return null;
  return [r, g, b, alpha].some((v) => !Number.isFinite(v)) ? null : [r, g, b, alpha];
}

// One color, as the Leaflet page showed it.
export function tintColor(text: string): string {
  const c = parseColor(text);
  if (!c) return text;
  const [r, g, b, a] = c;
  const s = SATURATE;
  // CSS saturate(): https://drafts.fxtf.org/filter-effects/#saturateEquivalent
  const sat = [
    (0.213 + 0.787 * s) * r + (0.715 - 0.715 * s) * g + (0.072 - 0.072 * s) * b,
    (0.213 - 0.213 * s) * r + (0.715 + 0.285 * s) * g + (0.072 - 0.072 * s) * b,
    (0.213 - 0.213 * s) * r + (0.715 - 0.715 * s) * g + (0.072 + 0.928 * s) * b,
  ];
  const [tr, tg, tb] = sat.map((v, i) =>
    Math.round(Math.max(0, Math.min(255, TILE_OPACITY * v + (1 - TILE_OPACITY) * (INK_RGB[i] ?? 0)))),
  );
  return `rgba(${tr}, ${tg}, ${tb}, ${+a.toFixed(3)})`;
}

// A paint value with its colors tinted: a color string, or a zoom function
// ({ stops: [[zoom, color], ...] }). Expressions are left alone (CARTO's
// dark style has none on colors).
function tintValue(v: unknown): unknown {
  if (typeof v === 'string') return tintColor(v);
  if (typeof v === 'object' && v !== null && !Array.isArray(v) && 'stops' in v && Array.isArray(v.stops)) {
    const stops: readonly unknown[] = v.stops;
    return {
      ...v,
      stops: stops.map((stop) => (Array.isArray(stop) && stop.length === 2 ? [stop[0], tintValue(stop[1])] : stop)),
    };
  }
  return v;
}

function isLayer(v: unknown): v is LayerSpecification {
  return typeof v === 'object' && v !== null && 'id' in v && typeof v.id === 'string' && 'type' in v;
}

// The basemap with every paint color tinted.
function tintStyle(style: StyleSpecification): StyleSpecification {
  const layers = style.layers.map((layer): LayerSpecification => {
    if (!('paint' in layer) || layer.paint === undefined) return layer;
    const paint = Object.fromEntries(
      Object.entries(layer.paint).map(([k, v]: readonly [string, unknown]) => [
        k,
        k.endsWith('-color') ? tintValue(v) : v,
      ]),
    );
    const tinted: unknown = { ...layer, paint };
    return isLayer(tinted) ? tinted : layer;
  });
  return { ...style, layers };
}

// ---- where you are

// Tinted, Dark Matter hides what tells you where you are: its parks,
// landcover and landuse are its background color, suburb names a dim #666
// from z12, and it draws admin levels 2, 4 and 6 only (London's boroughs are
// 8, Greater London 5; Sydney's councils, the City of Sydney among them, 6).
// These layers get their own colors, set after the tint and not tinted: land
// a little lighter than the page's ink, water and parks in their own hues, the
// CBD's commercial ground, suburbs (where OpenStreetMap maps them as areas, as
// Sydney's are) as faint outlined patches, quiet slate roads that brighten as
// you zoom in, dark buildings, boundaries as a faint dotted line, and place
// names bright on ink halos, and a coastline zoomed out only. No glow under
// the boundaries: councils end at the water, so it traced every inlet. All
// stay darker and less saturated than the lines, so the network stays on
// top. Water is darker than the land and nearly grey: a
// navy sea was the same hue as the blue lines running along it (London's
// Victoria and Piccadilly, Hong Kong's Island line, Sydney's T4).
const LAND = '#172044';
const WATER = '#0b1222';
const PARK = '#13302b';
const CBD = '#1f2951';
const SUBURB = 'rgba(190, 200, 250, 0.035)';
const SUBURB_EDGE = 'rgba(190, 200, 250, 0.16)';
const HALO = { 'text-halo-color': 'rgba(10, 18, 48, 0.85)', 'text-halo-width': 1.4 };
const WATER_TEXT = { 'text-color': '#8c9cc0', ...HALO };
// Cities (5), counties and councils (6), boroughs (8), on land.
const CITY_BOUNDARIES = ['all', ['in', 'admin_level', 5, 6, 8], ['==', 'maritime', 0]];

interface Override {
  readonly paint?: Readonly<Record<string, unknown>>;
  readonly layout?: Readonly<Record<string, unknown>>;
  readonly minzoom?: number;
  readonly filter?: unknown;
}

// Quiet zoomed out, where they'd compete with the lines, and brighter from
// ROAD_LIFT_FROM to ROAD_LIFT_TO, where they're what shows where you are.
const ROAD_LIFT_FROM = 14;
const ROAD_LIFT_TO = 16;
const roads = (ids: readonly string[], quiet: string, lifted: string): readonly (readonly [string, Override])[] =>
  ids.map((id) => [
    id,
    {
      paint: {
        'line-color': ['interpolate', ['linear'], ['zoom'], ROAD_LIFT_FROM, quiet, ROAD_LIFT_TO, lifted],
      },
    },
  ]);

// Dark Matter's layers, by id.
const OVERRIDES: ReadonlyMap<string, Override> = new Map<string, Override>([
  ['background', { paint: { 'background-color': LAND } }],
  ['landcover', { paint: { 'fill-color': PARK, 'fill-opacity': 0.9 } }],
  ['park_national_park', { paint: { 'fill-color': PARK, 'fill-opacity': 0.9 } }],
  ['park_nature_reserve', { paint: { 'fill-color': PARK, 'fill-opacity': 0.9 } }],
  ['landuse', { paint: { 'fill-color': PARK } }],
  ['landuse_residential', { paint: { 'fill-color': 'rgba(0, 0, 0, 0.12)' } }],
  ['water', { paint: { 'fill-color': WATER } }],
  ['building-top', { paint: { 'fill-color': '#10162f', 'fill-outline-color': '#2b3564' } }],
  ['waterway', { paint: { 'line-color': '#0b1222' } }],
  ...roads(['road_mot_fill_noramp', 'road_mot_fill_ramp', 'bridge_mot_fill', 'tunnel_mot_fill'], '#3b4670', '#5a68a3'),
  ...roads(
    ['road_trunk_fill_noramp', 'road_trunk_fill_ramp', 'bridge_trunk_fill', 'tunnel_trunk_fill'],
    '#343e66',
    '#4f5c95',
  ),
  ...roads(['road_pri_fill_noramp', 'road_pri_fill_ramp', 'bridge_pri_fill', 'tunnel_pri_fill'], '#2d365c', '#455188'),
  ...roads(['road_sec_fill_noramp', 'bridge_sec_fill', 'tunnel_sec_fill'], '#262e50', '#3a4577'),
  ...roads(['road_minor_fill', 'bridge_minor_fill', 'tunnel_minor_fill'], '#1f2645', '#313b69'),
  [
    'boundary_country_outline',
    {
      minzoom: 0,
      paint: {
        'line-color': '#080d1c',
        'line-opacity': curve([
          [4, 0],
          [5, 0],
          [6, 0.15],
          [8, 0.55],
        ]),
        'line-width': curve([
          [3, 1.6],
          [7, 3.4],
          [10, 4.0],
          [14, 4.75],
          [16, 5.2],
        ]),
        'line-dasharray': [1],
      },
    },
  ],
  [
    'boundary_country_inner',
    {
      paint: {
        'line-color': [
          'interpolate',
          ['linear'],
          ['zoom'],
          3,
          'rgba(220, 226, 250, 0.45)',
          6,
          'rgba(220, 226, 250, 0.6)',
          7,
          '#e4dbb5',
        ],
        'line-opacity': curve([
          [6, 1],
          [7, 0.875],
          [8, 0.9],
        ]),
        'line-width': curve([
          [1, 0.6],
          [3, 0.8],
          [6, 1.2],
          [7, 1.7],
          [10, 2.15],
          [14, 2.65],
          [16, 2.95],
        ]),
        'line-dasharray': [1],
      },
    },
  ],
  ['boundary_state', { paint: { 'line-color': 'rgba(220, 226, 250, 0.75)' } }],
  [
    'boundary_county',
    {
      filter: CITY_BOUNDARIES,
      minzoom: 8,
      paint: {
        'line-color': 'rgba(220, 226, 250, 0.45)',
        'line-width': {
          stops: [
            [8, 0.8],
            [12, 1],
            [15, 1.2],
          ],
        },
        'line-dasharray': [1.5, 2.5],
      },
    },
  ],
  ...[
    'place_city_r5',
    'place_city_r6',
    'place_city_dot_r2',
    'place_city_dot_r4',
    'place_city_dot_r7',
    'place_city_dot_z7',
    'place_capital_dot_z7',
  ].map((id): [string, Override] => [id, { paint: { 'text-color': '#eef0f8', ...HALO } }]),
  ['place_town', { paint: { 'text-color': '#d3d8ee', ...HALO } }],
  ['place_villages', { paint: { 'text-color': '#b9c0de', ...HALO } }],
  [
    'place_suburbs',
    {
      minzoom: 11,
      paint: { 'text-color': '#aeb6d8', ...HALO },
      layout: {
        'text-size': {
          stops: [
            [11, 10],
            [13, 11.5],
            [15, 13],
            [16, 14],
          ],
        },
      },
    },
  ],
  ['place_hamlet', { paint: { 'text-color': '#8f98bf', ...HALO } }],
  ['place_state', { paint: { 'text-color': '#c9d2f0', ...HALO } }],
  ...['watername_ocean', 'watername_sea', 'watername_lake', 'watername_lake_line', 'waterway_label'].map(
    (id): [string, Override] => [id, { paint: WATER_TEXT }],
  ),
  ['poi_park', { paint: { 'text-color': '#7fae98', ...HALO } }],
  ['poi_stadium', { paint: { 'text-color': '#8f98bf', ...HALO } }],
  ...['roadname_major', 'roadname_pri', 'roadname_sec', 'roadname_minor'].map((id): [string, Override] => [
    id,
    { paint: { 'text-color': '#8a93b8', ...HALO } },
  ]),
]);

// New layers, each drawn just before the Dark Matter layer named.
const ADDED: readonly (readonly [before: string, layer: LayerSpecification])[] = [
  [
    'landuse',
    {
      id: 'tol_suburbs',
      type: 'fill',
      source: 'carto',
      'source-layer': 'landuse',
      minzoom: 10,
      filter: ['==', 'class', 'suburb'],
      paint: { 'fill-color': SUBURB, 'fill-outline-color': 'rgba(0, 0, 0, 0)' },
    },
  ],
  [
    'landuse',
    {
      id: 'tol_suburb_edges',
      type: 'line',
      source: 'carto',
      'source-layer': 'landuse',
      minzoom: 10,
      filter: ['==', 'class', 'suburb'],
      paint: {
        'line-color': SUBURB_EDGE,
        'line-width': curve([
          [10, 0.6],
          [14, 1.2],
        ]),
      },
    },
  ],
  [
    'landuse',
    {
      id: 'tol_cbd',
      type: 'fill',
      source: 'carto',
      'source-layer': 'landuse',
      minzoom: 9,
      filter: ['in', 'class', 'commercial', 'retail'],
      paint: { 'fill-color': CBD },
    },
  ],
  // The continents' edge, zoomed out only: from a city's zooms, where the
  // shore is a council boundary and a coastline would trace every inlet,
  // the water's own color is enough.
  [
    'water_shadow',
    {
      id: 'tol_coastline',
      type: 'line',
      source: 'carto',
      'source-layer': 'water',
      maxzoom: 7,
      filter: ['==', '$type', 'Polygon'],
      paint: {
        'line-color': 'rgba(150, 170, 215, 0.4)',
        'line-width': 0.6,
        'line-opacity': curve([
          [5, 1],
          [7, 0],
        ]),
      },
    },
  ],
];

function override(layer: LayerSpecification, o: Override | undefined): LayerSpecification {
  if (o === undefined) return layer;
  const merged: unknown = {
    ...layer,
    ...(o.minzoom === undefined ? {} : { minzoom: o.minzoom }),
    ...(o.filter === undefined ? {} : { filter: o.filter }),
    ...(o.paint === undefined ? {} : { paint: { ...('paint' in layer ? layer.paint : {}), ...o.paint } }),
    ...(o.layout === undefined ? {} : { layout: { ...layer.layout, ...o.layout } }),
  };
  return isLayer(merged) ? merged : layer;
}

// The basemap's credit, as CARTO asks for it (https://carto.com/attribution/):
// "The word CARTO links to this page; OpenStreetMap links to its copyright
// page." CARTO's TileJSON links elsewhere (/about-carto/, which redirects to
// its product page, and OpenStreetMap's /about/); a source's own attribution
// takes precedence over its TileJSON's in MapLibre.
export const BASEMAP_CREDIT =
  '&copy; <a href="https://www.openstreetmap.org/copyright" target="_blank" rel="noopener">OpenStreetMap</a> contributors, &copy; <a href="https://carto.com/attribution/" target="_blank" rel="noopener">CARTO</a>';

// How much larger the map's text is than CARTO's (8-12 px street and place
// names, too small to read on a desktop screen) and the network's own sizes:
// by `scale`, but never more than MAX_GAIN px, so the largest (a city's name)
// barely grow. A phone's is less: its screen is held closer, and small.
export const TEXT_SCALE = { desktop: 1.3, phone: 1.1 } as const;
// Station names are bold and were legible already: they grow at most as much
// as on a phone, or dense networks (Toronto's streetcar stops) crowd.
const STATION_SCALE = (s: number): number => Math.min(s, TEXT_SCALE.phone);
const MAX_GAIN = 3;
export const enlargeText = (px: number, scale: number): number =>
  Math.round((px + Math.min(px * (scale - 1), MAX_GAIN)) * 10) / 10;

// A symbol layer's text-size enlarged: a number, or a legacy zoom function's
// stops, as CARTO writes them; anything else as it is.
function enlargeLayer(layer: LayerSpecification, scale: number): LayerSpecification {
  if (layer.type !== 'symbol' || scale === 1) return layer;
  const size: unknown = layer.layout?.['text-size'];
  let enlarged: unknown;
  if (typeof size === 'number') enlarged = enlargeText(size, scale);
  else if (typeof size === 'object' && size !== null && 'stops' in size && Array.isArray(size.stops)) {
    const stops: unknown[] = size.stops;
    if (!stops.every((s) => Array.isArray(s) && typeof s[0] === 'number' && typeof s[1] === 'number')) return layer;
    enlarged = { ...size, stops: stops.map((s) => (Array.isArray(s) ? [s[0], enlargeText(Number(s[1]), scale)] : s)) };
  } else return layer;
  const merged: unknown = { ...layer, layout: { ...layer.layout, 'text-size': enlarged } };
  return isLayer(merged) ? merged : layer;
}

// CARTO's Dark Matter as the page draws it: tinted to the ink, then made
// legible (its text enlarged by `textScale`, TEXT_SCALE), with its credit.
// If CARTO renames a layer, it keeps just its tint, and a layer added before
// it is left out.
export function basemapStyle(style: StyleSpecification, textScale = 1): StyleSpecification {
  const layers: LayerSpecification[] = [];
  for (const layer of tintStyle(style).layers) {
    for (const [before, added] of ADDED) if (before === layer.id) layers.push(added);
    const styled = enlargeLayer(override(layer, OVERRIDES.get(layer.id)), textScale);
    if (layer.id === 'boundary_country_outline' || layer.id === 'boundary_country_inner') {
      if (styled.type !== 'line') continue;
      const { 'source-layer': _sourceLayer, filter: _filter, maxzoom: _maxzoom, ...border } = styled;
      for (const [suffix, source, minzoom, maxzoom] of [
        ['low', 'country-borders-110m', 0, 3],
        ['low_50m', 'country-borders-50m', 3, 5],
        ['low_10m', 'country-borders', 5, COUNTRY_BORDER_DETAIL_ZOOM],
      ] as const)
        layers.push({
          ...border,
          id: `${layer.id}_${suffix}`,
          source,
          minzoom,
          maxzoom,
        });
      layers.push({ ...styled, minzoom: COUNTRY_BORDER_DETAIL_ZOOM });
    } else layers.push(styled);
  }
  const carto = style.sources['carto'];
  const sources =
    carto?.type === 'vector' ? { ...style.sources, carto: { ...carto, attribution: BASEMAP_CREDIT } } : style.sources;
  return {
    ...style,
    sources: {
      ...sources,
      'country-borders-110m': { type: 'geojson', data: COUNTRY_BORDERS_110M, tolerance: 0, maxzoom: 24 },
      'country-borders-50m': { type: 'geojson', data: COUNTRY_BORDERS_50M, tolerance: 0, maxzoom: 24 },
      'country-borders': { type: 'geojson', data: COUNTRY_BORDERS, tolerance: 0, maxzoom: 24 },
    },
    layers,
  };
}

export function isStyle(v: unknown): v is StyleSpecification {
  return (
    typeof v === 'object' &&
    v !== null &&
    'version' in v &&
    v.version === 8 &&
    'sources' in v &&
    typeof v.sources === 'object' &&
    'layers' in v &&
    Array.isArray(v.layers) &&
    v.layers.every(isLayer)
  );
}

// ---- zoom expressions, from the Leaflet page's functions

// A zoom curve: [MapLibre zoom, value] stops, linear between them and clamped
// outside, like MapLibre's 'interpolate'.
type Stops = readonly (readonly [zoom: number, value: number])[];
// What a stop's value turns into: a number, or an expression of the feature.
type StopValue = number | ExpressionSpecification;

// A MapLibre zoom expression from stops. MapLibre only allows ['zoom'] as the
// input of a top-level interpolate, so anything that varies per feature (a
// 'case' or a product) goes inside each stop's output.
function curve(stops: Stops, value: (v: number) => StopValue = (v) => v): ExpressionSpecification {
  return ['interpolate', ['linear'], ['zoom'], ...stops.flatMap(([z, v]) => [z, value(v)])];
}

// A clamped linear function of Leaflet zoom, v = a + (zL - z0) * k clamped to
// [lo, hi], as stops over MapLibre zoom.
function clampedLinear(
  a: number,
  z0: number,
  k: number,
  lo: number,
  hi: number,
): readonly [readonly [number, number], readonly [number, number]] {
  return [
    [ml(z0 + (lo - a) / k), lo],
    [ml(z0 + (hi - a) / k), hi],
  ];
}

// The Leaflet page's line weight: clamp(1.3 + (z - 10) * 0.75, 1.2, 6.5).
const lineWeight = clampedLinear(1.3, 10, 0.75, 1.2, 6.5);
// render.ts railGap: 5.2 * trainScale(z) / 2 + 0.6, with trainScale =
// clamp(0.62 + (z - 10) * 0.2, 0.55, 1.55).
const railGap = clampedLinear(5.2 * 0.31 + 0.6, 10, 0.52, 5.2 * 0.275 + 0.6, 5.2 * 0.775 + 0.6);
// render.ts stationRadius / stationLine.
const stationRadius = clampedLinear(2.2, 11, 0.55, 2.2, 4.4);
const stationLine = clampedLinear(1.2, 11, 0.2, 1.2, 1.8);

const weightAt = (zl: number): number => Math.max(1.2, Math.min(6.5, 1.3 + (zl - 10) * 0.75));
const gapAt = (zl: number): number => (5.2 * Math.max(0.55, Math.min(1.55, 0.62 + (zl - 10) * 0.2))) / 2 + 0.6;
// Tracks spread from the center as they fade in. Keep their buckets present
// at every zoom: retained parent tiles must draw them while finer tiles load.
const splittingGap: Stops = [
  [SPLIT_FROM, 0],
  [SPLIT_FULL, gapAt(SPLIT_FULL + ZOOM_OFFSET)],
  ...railGap.filter(([z]) => z > SPLIT_FULL),
];
// The glow around two tracks is gap * 2 + w * 2. Both are piecewise linear, so
// sampling at both functions' breaks is exact.
const splitGlow: Stops = [9.65, 9.867, 14.65, 16.933].map((zl) => [ml(zl), gapAt(zl) * 2 + weightAt(zl) * 2]);

const isInter: ExpressionSpecification = ['==', ['get', 'inter'], true];

// ---- data

// side: 1 and -1 for a line's two tracks, zoomed in; zoomed out, side 1's
// feature is the line itself. (A third copy for that, of every path, made
// 589,000 points for the US Northeast's networks, which MapLibre serializes
// for its worker and tiles every time the world map's set of cities changes:
// seconds of work on each zoom in or out.) Both tracks stay in one layer, so
// line-sort-key orders them where two lines share a corridor in opposite
// directions and one's left track is the other's right. A minor line is one
// line at every zoom, side 1.
// order: the line's place in LINES, so each line's tracks draw together and a
// later line covers an earlier one on shared track.
type LineFeature = GeoJSON.Feature<
  GeoJSON.LineString,
  {
    line: LineId;
    color: string;
    glow: string;
    casing: boolean;
    tube: boolean;
    dual: boolean;
    minor: boolean;
    side: number;
    order: number;
  }
>;

// Keep one exact viewport result per immutable layout. Other cities arriving
// must not simplify this city's unchanged track again. Weak keys follow the
// existing retained-network lifetime rather than accumulating past views.
const preparedPaths = new WeakMap<
  LineLayout,
  {
    readonly bounds: Box | undefined;
    readonly tolerance: number;
    readonly paths: readonly (readonly LatLngTuple[])[];
  }
>();

export function linesGeoJSON(
  lines: readonly PageLine[],
  layouts: ReadonlyMap<LineId, LineLayout>,
  bounds?: Box,
  toleranceMeters = 0,
): GeoJSON.FeatureCollection<GeoJSON.LineString> {
  return finish(lineDataSteps(lines, layouts, bounds, toleranceMeters));
}

export function linesGeoJSONAsync(
  lines: readonly PageLine[],
  layouts: ReadonlyMap<LineId, LineLayout>,
  bounds: Box,
  toleranceMeters: number,
  signal: AbortSignal,
): Promise<GeoJSON.FeatureCollection<GeoJSON.LineString>> {
  return cooperate(lineDataSteps(lines, layouts, bounds, toleranceMeters), signal);
}

function* lineDataSteps(
  lines: readonly PageLine[],
  layouts: ReadonlyMap<LineId, LineLayout>,
  bounds: Box | undefined,
  toleranceMeters: number,
): Generator<void, GeoJSON.FeatureCollection<GeoJSON.LineString>, void> {
  const features: LineFeature[] = [];
  for (const [order, l] of lines.entries()) {
    const layout = layouts.get(l.id);
    if (!layout) continue;
    const cached = preparedPaths.get(layout);
    let paths = cached?.paths;
    if (
      !cached ||
      cached.tolerance !== toleranceMeters ||
      (bounds === undefined
        ? cached.bounds !== undefined
        : cached.bounds === undefined || bounds.some((v, i) => v !== cached.bounds?.[i]))
    ) {
      const prepared: (readonly LatLngTuple[])[] = [];
      for (const path of layout.paths) {
        yield;
        for (const visible of bounds ? visiblePaths(path, bounds) : [path]) {
          yield;
          prepared.push(toleranceMeters > 0 ? simplify(visible, toleranceMeters) : visible);
        }
      }
      paths = prepared;
      preparedPaths.set(layout, { bounds, tolerance: toleranceMeters, paths });
    }
    for (const points of paths ?? []) {
      yield;
      const coordinates = points.map(([lat, lon]) => [lon, lat]);
      for (const side of l.minor === true ? [1] : [1, -1]) {
        features.push({
          type: 'Feature',
          properties: {
            line: l.id,
            color: l.show,
            // A dark line glows, and is outlined, in CASING.
            glow: isDark(l.show) ? CASING : l.show,
            casing: isDark(l.show),
            tube: l.mode === 'tube',
            dual: l.dual,
            minor: l.minor === true,
            side,
            order,
          },
          geometry: { type: 'LineString', coordinates },
        });
      }
    }
  }
  return { type: 'FeatureCollection', features };
}

// Which stations the map shows, and how: a station is on a shown major line;
// a stop is served by shown minor lines only (a bus stop, minor.ts). A stop
// on no shown line is neither.
export function stationTiers(
  net: Network,
  lines: MinorLines,
  hidden: ReadonlySet<LineId>,
): { readonly stations: ReadonlySet<StationId>; readonly stops: ReadonlySet<StationId> } {
  const stations = new Set<StationId>();
  const stops = new Set<StationId>();
  for (const st of net.stations.values()) {
    let minor = false;
    for (const id of st.lines) {
      if (lines[id] === undefined || hidden.has(id)) continue;
      if (!isMinor(lines, id)) {
        stations.add(st.id);
        minor = false;
        break;
      }
      minor = true;
    }
    if (minor) stops.add(st.id);
  }
  return { stations, stops };
}

// Stops (stationTiers) as points: just where each is and its name.
export function stopsGeoJSON(net: Network, stops: ReadonlySet<StationId>): GeoJSON.FeatureCollection<GeoJSON.Point> {
  const features: GeoJSON.Feature<GeoJSON.Point>[] = [];
  for (const id of stops) {
    const st = net.stations.get(id);
    if (st)
      features.push({
        type: 'Feature',
        properties: { id: st.id, name: st.name },
        geometry: { type: 'Point', coordinates: [st.lon, st.lat] },
      });
  }
  return { type: 'FeatureCollection', features };
}

// Systems whose lines run in the street and stop every few hundred meters.
// Their stops are named only zoomed in (stationLayers).
export const STREET_SYSTEMS: ReadonlySet<string> = new Set([
  'tram',
  'trams',
  'streetcar',
  'trolleybus',
  'bus',
  'buses',
]);

// A station named at city zoom: one on a major line that isn't a
// street line (a metro, railway, light rail or ferry stop). A line without a
// system (London's) counts as one.
function isMajor(st: Pick<Station, 'lines'>, lines: Parameters<typeof isInterchange>[1]): boolean {
  for (const id of st.lines) {
    const l = lines[id];
    if (l && l.minor !== true && !STREET_SYSTEMS.has(l.system ?? '')) return true;
  }
  return false;
}

// A multi-line station useful enough to name at city zoom: one where a
// shown major service terminates or branches, or where the major lines
// follow different local corridors.
// Parallel services sharing the same two neighbours do not turn every stop
// along their common trunk into an early label.
function isPriorityStation(
  st: Pick<Station, 'id' | 'lines'>,
  net: Network,
  lines: Parameters<typeof isInterchange>[1],
): boolean {
  const neighborhoods: ReadonlySet<StationId>[] = [];
  for (const id of st.lines) {
    const style = lines[id];
    if (!style || style.minor === true) continue;
    const adjacent = net.lines[id]?.adj.get(st.id);
    if (adjacent) neighborhoods.push(adjacent);
  }
  if (neighborhoods.length < 2) return false;
  if (neighborhoods.some((adjacent) => adjacent.size !== 2)) return true;
  const signatures = neighborhoods.map((adjacent) => [...adjacent].toSorted().join('\0'));
  return new Set(signatures).size > 1;
}

// Stations on a shown line, and for labels one station per name: TfL lists
// some complexes as separate stops (Underground vs Elizabeth line vs DLR), so
// a name can have several dots within a few hundred meters. Label the
// best-connected one.
export function stationsGeoJSON(
  net: Network,
  visible: ReadonlySet<StationId>,
  lines: Parameters<typeof isInterchange>[1],
  dist: typeof Core.dist,
  labelDistance = 600,
): GeoJSON.FeatureCollection<GeoJSON.Point> {
  const shown = [...net.stations.values()]
    .filter((st) => visible.has(st.id))
    .toSorted((a, b) => b.lines.size - a.lines.size);
  const labeled = new Set<StationId>();
  const kept = new Map<string, (typeof shown)[number][]>();
  for (const st of shown) {
    const name = st.name.trim().toLowerCase().replaceAll(/\s+/gu, ' ');
    const named = kept.get(name) ?? [];
    if (named.some((o) => dist(o, st) < labelDistance)) continue;
    named.push(st);
    kept.set(name, named);
    labeled.add(st.id);
  }
  return {
    type: 'FeatureCollection',
    features: shown.map((st) => ({
      type: 'Feature',
      properties: {
        id: st.id,
        name: st.name,
        inter: isInterchange(st, lines),
        label: labeled.has(st.id),
        rank: -st.lines.size,
        major: isMajor(st, lines),
        priority: isPriorityStation(st, net, lines),
      },
      geometry: { type: 'Point', coordinates: [st.lon, st.lat] },
    })),
  };
}

// ---- layers

const MAJOR: ExpressionSpecification = ['!=', ['get', 'minor'], true];
const TUBE: ExpressionSpecification = ['all', MAJOR, ['==', ['get', 'tube'], true]];
const NOT_TUBE: ExpressionSpecification = ['all', MAJOR, ['==', ['get', 'tube'], false]];
const DUAL: ExpressionSpecification = ['==', ['get', 'dual'], true];

// A network line layer: its filter before hidden lines are taken out, the
// opacity that hover focus dims (null: not dimmed), and the MapLibre zooms
// it fades in between, if it does.
interface LineLayer {
  readonly spec: LayerSpecification & { readonly type: 'line' };
  readonly filter: ExpressionSpecification;
  readonly opacity: number | null;
  readonly fade?: readonly [from: number, full: number];
  readonly fadeOut?: readonly [full: number, gone: number];
}

// A layer's line-opacity: its own (DIM of it for lines other than the
// focused one), faded in over its zooms if it fades.
function lineOpacity(
  layer: Pick<LineLayer, 'fade' | 'fadeOut'>,
  opacity: number,
  focus: LineId | null,
): number | ExpressionSpecification {
  const value: number | ExpressionSpecification =
    focus === null ? opacity : ['case', ['==', ['get', 'line'], focus], opacity, opacity * DIM];
  if (layer.fade) return ['interpolate', ['linear'], ['zoom'], layer.fade[0], 0, layer.fade[1], value];
  if (layer.fadeOut) return ['interpolate', ['linear'], ['zoom'], layer.fadeOut[0], value, layer.fadeOut[1], 0];
  return value;
}

// Other lines' opacity while one line is focused, as a share of their own.
const DIM = 0.22;

// The layers for one group of lines (Overground, DLR and Elizabeth first, the
// Tube on top). Unsplit: a glow, the line and, for Overground, an ink center
// (hollow). Around SPLIT_ZOOM they fade into a wider glow and two tracks
// spreading either side with line-offset, dashed for Overground. Opacity
// controls the handoff; zoom visibility limits omit needed worker buckets.
function lineGroup(group: 'tube' | 'other'): LineLayer[] {
  const handoff = [SPLIT_FROM, SPLIT_FULL] as const;
  const filter = group === 'tube' ? TUBE : NOT_TUBE;
  const color: ExpressionSpecification = ['get', 'color'];
  const center: ExpressionSpecification = ['all', filter, ['==', ['get', 'side'], 1]];
  const hollow: ExpressionSpecification = ['all', center, DUAL];
  const glow: ExpressionSpecification = ['get', 'glow'];
  const cased: ExpressionSpecification = ['==', ['get', 'casing'], true];
  // Both tracks in one layer, offset by side * railGap, sorted by line.
  const rails = (dashed: boolean): LineLayer => {
    const railFilter: ExpressionSpecification = ['all', filter, ['==', ['get', 'dual'], dashed]];
    return {
      filter: railFilter,
      opacity: 0.92,
      fade: handoff,
      spec: {
        id: `${group}-rails${dashed ? '-dashed' : ''}`,
        type: 'line',
        source: 'lines',
        filter: railFilter,
        layout: { 'line-cap': dashed ? 'butt' : 'round', 'line-join': 'round', 'line-sort-key': ['get', 'order'] },
        paint: {
          'line-color': color,
          'line-opacity': 0.92,
          // MapLibre scales dashes using line width at floor(zoom). Keep
          // that width constant through the split, so z12 cannot jump the
          // entire dash pattern while the track itself moves smoothly.
          'line-width': dashed
            ? curve([
                [SPLIT_ZOOM + 1, Math.max(1.4, weightAt(SPLIT_Z + 1) * 0.5)],
                [lineWeight[1][0], 3.25],
              ])
            : curve(lineWeight, (w) => Math.max(1.4, w * 0.5)),
          'line-offset': curve(splittingGap, (g) => ['*', ['get', 'side'], g]),
          // In line widths: the Leaflet page dashed '7 4' px at a ~2-3 px rail.
          ...(dashed ? { 'line-dasharray': [2.8, 1.6] } : {}),
        },
      },
    };
  };
  const layers: LineLayer[] = [
    {
      filter: center,
      opacity: 0.13,
      fadeOut: handoff,
      spec: {
        id: `${group}-glow`,
        type: 'line',
        source: 'lines',
        filter: center,
        layout: { 'line-cap': 'round', 'line-join': 'round' },
        paint: { 'line-color': glow, 'line-opacity': 0.13, 'line-width': curve(lineWeight, (w) => w * 3.4) },
      },
    },
    {
      filter: center,
      opacity: 0.13,
      fade: handoff,
      spec: {
        id: `${group}-glow-split`,
        type: 'line',
        source: 'lines',
        filter: center,
        layout: { 'line-cap': 'round', 'line-join': 'round', 'line-sort-key': ['get', 'order'] },
        paint: { 'line-color': glow, 'line-opacity': 0.13, 'line-width': curve(splitGlow) },
      },
    },
    // A light outline under dark lines, zoomed out and in.
    {
      filter: ['all', center, cased],
      opacity: 0.92,
      fadeOut: handoff,
      spec: {
        id: `${group}-casing`,
        type: 'line',
        source: 'lines',
        filter: ['all', center, cased],
        layout: { 'line-cap': 'round', 'line-join': 'round', 'line-sort-key': ['get', 'order'] },
        paint: { 'line-color': CASING, 'line-opacity': 0.92, 'line-width': curve(lineWeight, (w) => w + 2) },
      },
    },
    {
      filter: ['all', filter, cased],
      opacity: 0.92,
      fade: handoff,
      spec: {
        id: `${group}-rails-casing`,
        type: 'line',
        source: 'lines',
        filter: ['all', filter, cased],
        layout: { 'line-cap': 'round', 'line-join': 'round', 'line-sort-key': ['get', 'order'] },
        paint: {
          'line-color': CASING,
          'line-opacity': 0.92,
          'line-width': curve(lineWeight, (w) => Math.max(1.4, w * 0.5) + 1.6),
          'line-offset': curve(splittingGap, (g) => ['*', ['get', 'side'], g]),
        },
      },
    },
    {
      filter: center,
      opacity: 0.92,
      fadeOut: handoff,
      spec: {
        id: `${group}-core`,
        type: 'line',
        source: 'lines',
        filter: center,
        layout: { 'line-cap': 'round', 'line-join': 'round', 'line-sort-key': ['get', 'order'] },
        paint: { 'line-color': color, 'line-opacity': 0.92, 'line-width': curve(lineWeight) },
      },
    },
    {
      filter: hollow,
      opacity: null,
      fadeOut: handoff,
      spec: {
        id: `${group}-inner`,
        type: 'line',
        source: 'lines',
        filter: hollow,
        layout: { 'line-cap': 'round', 'line-join': 'round' },
        paint: { 'line-color': INK, 'line-width': curve(lineWeight, (w) => Math.max(0.8, w * 0.36)) },
      },
    },
    rails(false),
    rails(true),
  ];
  const faded: LineLayer[] = [];
  for (const layer of layers) {
    faded.push({
      ...layer,
      spec: {
        ...layer.spec,
        paint: {
          ...layer.spec.paint,
          'line-opacity': lineOpacity(layer, layer.opacity ?? 1, null),
        },
      },
    });
  }
  return faded;
}

// Minor lines (minor.ts): one thin, muted line under every major one, from
// MINOR.LINE_FROM.
function minorGroup(): LineLayer[] {
  const filter: ExpressionSpecification = ['==', ['get', 'minor'], true];
  const fade = [ml(MINOR.LINE_FROM), ml(MINOR.LINE_FULL)] as const;
  return [
    {
      filter,
      opacity: MINOR.LINE_OPACITY,
      fade,
      spec: {
        id: 'minor-core',
        type: 'line',
        source: 'lines',
        filter,
        minzoom: fade[0],
        layout: { 'line-cap': 'round', 'line-join': 'round', 'line-sort-key': ['get', 'order'] },
        paint: {
          'line-color': ['get', 'color'],
          'line-opacity': lineOpacity({ fade }, MINOR.LINE_OPACITY, null),
          'line-width': curve(lineWeight, (w) => Math.max(1, w * MINOR.LINE_WIDTH)),
        },
      },
    },
  ];
}

const LINE_LAYERS: readonly LineLayer[] = [...minorGroup(), ...lineGroup('other'), ...lineGroup('tube')];
// The lowest line layer, for drawing National Rail track under the TfL lines.
export const LOWEST_LINE_LAYER = LINE_LAYERS[0]?.spec.id;

// The station dots layer, which clicks and hover tooltips query.
export const STATION_LAYER = 'stations';
// National Rail stations that aren't also TfL stations (nr-stations.ts).
export const NR_STATION_LAYER = 'nr-stations';
// Stops only minor lines serve (stationTiers), which clicks also query.
export const STOP_LAYER = 'stops';

// A stop: a solid light gray dot ringed in ink, fading in from
// MINOR.STOP_Z, and its name from MINOR.STOP_NAME_Z, both under the
// stations (and their names, which win a clash). A station is a larger
// hollow ring (4.4 px zoomed in, to the stop's 3.6), so a stop reads as a lesser one.
function stopLayers(fonts: { readonly regular: readonly string[] } | null, textScale: number): LayerSpecification[] {
  const from = ml(MINOR.STOP_Z);
  const dots: LayerSpecification = {
    id: STOP_LAYER,
    type: 'circle',
    source: STOP_LAYER,
    minzoom: from,
    paint: {
      'circle-radius': curve([
        [from, 3],
        [ml(18), 3.6],
      ]),
      'circle-color': '#B9BFD6',
      'circle-stroke-color': INK,
      'circle-stroke-width': 1.3,
      'circle-opacity': curve([
        [from, 0],
        [from + 0.4, 1],
      ]),
      'circle-stroke-opacity': curve([
        [from, 0],
        [from + 0.4, 1],
      ]),
    },
  };
  if (!fonts) return [dots];
  return [
    dots,
    {
      id: 'stop-names',
      type: 'symbol',
      source: STOP_LAYER,
      minzoom: ml(MINOR.STOP_NAME_Z),
      layout: {
        'text-field': ['get', 'name'],
        'text-font': [...fonts.regular],
        'text-size': enlargeText(10.5, textScale),
        'text-variable-anchor': ['left', 'right', 'bottom', 'top'],
        'text-radial-offset': 0.5,
        'text-justify': 'auto',
        'text-padding': 2,
      },
      paint: {
        'text-color': '#AAB1CC',
        'text-halo-color': 'rgba(10, 18, 48, 0.92)',
        'text-halo-width': 1.5,
      },
    },
  ];
}

// Station dots (Leaflet zoom 11+) and names (topology-changing hubs and
// termini from 12, all from 13). MapLibre places labels with collision detection, trying right, left,
// above and below the dot, and gives earlier layers and lower sort keys
// priority. Those two zooms name only the major stations (isMajor: not a
// tram or streetcar stop), on a phone and a desktop alike; every other
// station, an interchange of two streetcar routes too, is named from
// STREET_NAMES_Z (street level), in smaller, dimmer type with more room
// around it, or a city's hundreds of tram stops ("Queen St East at Kingston
// Rd") cover its trains and its rail stations' names. On a phone the names
// are smaller.
const STREET_NAMES_Z = ml(15.5);
function stationLayers(
  fonts: { readonly bold: readonly string[]; readonly regular: readonly string[] } | null,
  textScale: number,
  phone: boolean,
): LayerSpecification[] {
  const dots: LayerSpecification = {
    id: STATION_LAYER,
    type: 'circle',
    source: 'stations',
    minzoom: ml(11),
    paint: {
      'circle-radius': curve(stationRadius, (r) => ['case', isInter, r * 1.25, r]),
      'circle-color': ['case', isInter, '#F4F6FC', INK],
      'circle-stroke-color': ['case', isInter, INK, '#EEF0F8'],
      'circle-stroke-width': curve(stationLine),
    },
  };
  if (!fonts) return [dots];
  const isMajorStation: ExpressionSpecification = ['==', ['get', 'major'], true];
  const size = (px: number): number => (phone ? px - 1 : enlargeText(px, STATION_SCALE(textScale)));
  const label = (
    id: string,
    minzoom: number,
    inter: boolean | null,
    major: boolean | null,
    priority = false,
    maxzoom?: number,
  ): LayerSpecification => ({
    id,
    type: 'symbol',
    source: 'stations',
    minzoom,
    ...(maxzoom === undefined ? {} : { maxzoom }),
    filter: [
      'all',
      ['==', ['get', 'label'], true],
      ...(inter === null ? [] : [['==', ['get', 'inter'], inter] satisfies ExpressionSpecification]),
      ...(major === null ? [] : [major ? isMajorStation : (['!', isMajorStation] satisfies ExpressionSpecification)]),
      ...(priority ? [['==', ['get', 'priority'], true] satisfies ExpressionSpecification] : []),
    ],
    layout: {
      'text-field': ['get', 'name'],
      'text-font': [...(inter === true ? fonts.bold : fonts.regular)],
      'text-size':
        major === false
          ? ['step', ['zoom'], size(10.5), ml(16.5), size(11.5)]
          : ['step', ['zoom'], size(11.5), ml(14), size(12.5)],
      'text-variable-anchor': ['left', 'right', 'bottom', 'top'],
      'text-radial-offset': 0.6,
      'text-justify': 'auto',
      'symbol-sort-key': ['get', 'rank'],
      'text-padding': major === false ? 6 : 2,
    },
    paint: {
      'text-color': inter === true ? '#FFFFFF' : major === false ? '#AEB4CC' : '#CDD2E4',
      'text-halo-color': 'rgba(10, 18, 48, 0.92)',
      'text-halo-width': 1.75,
    },
  });
  // Placement priority runs top layer first, so interchanges go on top.
  return [
    dots,
    label('station-names-minor', STREET_NAMES_Z, null, false),
    label('station-names', ml(13), false, true),
    label('station-names-inter', ml(13), true, true),
    label('station-names-priority-inter', ml(12), true, true, true, ml(13)),
  ];
}

// National Rail station dots: like a TfL station's, with the National Rail
// track's gray for the ring, and names from Leaflet zoom 13 (placed after
// TfL's, so TfL names win a clash).
function nrStationLayers(
  fonts: { readonly regular: readonly string[] } | null,
  textScale: number,
): LayerSpecification[] {
  const dots: LayerSpecification = {
    id: NR_STATION_LAYER,
    type: 'circle',
    source: NR_STATION_LAYER,
    minzoom: ml(11),
    paint: {
      'circle-radius': curve(stationRadius),
      'circle-color': INK,
      'circle-stroke-color': '#A3A9C0',
      'circle-stroke-width': curve(stationLine),
    },
  };
  if (!fonts) return [dots];
  return [
    dots,
    {
      id: 'nr-station-names',
      type: 'symbol',
      source: NR_STATION_LAYER,
      minzoom: ml(13),
      layout: {
        'text-field': ['get', 'name'],
        'text-font': [...fonts.regular],
        'text-size': [
          'step',
          ['zoom'],
          enlargeText(11, STATION_SCALE(textScale)),
          ml(14),
          enlargeText(12, STATION_SCALE(textScale)),
        ],
        'text-variable-anchor': ['left', 'right', 'bottom', 'top'],
        'text-radial-offset': 0.6,
        'text-justify': 'auto',
        'text-padding': 2,
      },
      paint: {
        'text-color': '#B4BACE',
        'text-halo-color': 'rgba(10, 18, 48, 0.92)',
        'text-halo-width': 1.75,
      },
    },
  ];
}

// What addNetworkLayers uses of the map.
export interface LayerHost {
  readonly addSource: (id: string, source: GeoJSONSourceSpecification) => unknown;
  readonly addLayer: (layer: LayerSpecification, before?: string) => unknown;
  readonly getLayersOrder: () => string[];
  readonly getLayer: (id: string) => { readonly type: string } | undefined;
}

// Add the network's sources and layers. Lines go under the basemap's place
// labels (so street and area names stay readable over them); stations and
// their names go on top of everything. Their names are enlarged by
// `textScale` (TEXT_SCALE), as the basemap's are, or smaller and fewer on a phone.
export function addNetworkLayers(map: LayerHost, hasGlyphs: boolean, textScale = 1, phone = false): void {
  map.addSource('lines', { type: 'geojson', data: { type: 'FeatureCollection', features: [] } });
  map.addSource('stations', { type: 'geojson', data: { type: 'FeatureCollection', features: [] } });
  const firstLabel = map.getLayersOrder().find((id) => map.getLayer(id)?.type === 'symbol');
  const beforeBorders = map.getLayer('boundary_country_outline_low')
    ? 'boundary_country_outline_low'
    : map.getLayer('boundary_country_outline')
      ? 'boundary_country_outline'
      : firstLabel;
  for (const { spec } of LINE_LAYERS)
    map.addLayer(spec, spec.id.includes('glow') || spec.id.includes('casing') ? beforeBorders : firstLabel);
  // CARTO's glyph server serves the style's own font stacks.
  const tail = ['Noto Sans Regular', 'HanWangHeiLight Regular', 'NanumBarunGothic Regular'];
  const fonts = hasGlyphs
    ? {
        bold: ['Montserrat Medium', 'Open Sans Bold', ...tail],
        regular: ['Montserrat Regular', 'Open Sans Regular', ...tail],
      }
    : null;
  map.addSource(NR_STATION_LAYER, { type: 'geojson', data: { type: 'FeatureCollection', features: [] } });
  map.addSource(STOP_LAYER, { type: 'geojson', data: { type: 'FeatureCollection', features: [] } });
  // Under the TfL stations and their names; stops under both.
  const firstStation = STATION_LAYER;
  for (const layer of stationLayers(fonts, textScale, phone)) map.addLayer(layer);
  for (const layer of nrStationLayers(fonts, textScale)) map.addLayer(layer, firstStation);
  const firstNr = NR_STATION_LAYER;
  for (const layer of stopLayers(fonts, textScale)) map.addLayer(layer, firstNr);
}

// Resize existing labels without replacing network sources or visibility filters.
export function updateNetworkTextSize(map: MapLibreMap, textScale: number, phone: boolean): void {
  const fonts = { bold: [], regular: [] };
  for (const layer of [
    ...stationLayers(fonts, textScale, phone),
    ...nrStationLayers(fonts, textScale),
    ...stopLayers(fonts, textScale),
  ]) {
    if (layer.type === 'symbol' && layer.layout?.['text-size'] !== undefined && map.getLayer(layer.id)) {
      map.setLayoutProperty(layer.id, 'text-size', layer.layout['text-size']);
    }
  }
}

// World ids start with a region prefix. Match whole regions without sending
// thousands of retained line or station ids through MapLibre's filters.
export function regionFilter(property: 'line' | 'id', regions: readonly string[]): ExpressionSpecification {
  return ['in', ['slice', ['get', property], 0, ['index-of', ':', ['get', property]]], ['literal', [...regions]]];
}

// Hide lines (the line switches in the panel), optionally within active world regions.
export function filterLines(map: MapLibreMap, hidden: ReadonlySet<LineId>, regions?: readonly string[]): void {
  const shown: ExpressionSpecification | null =
    hidden.size === 0 ? null : ['!', ['in', ['get', 'line'], ['literal', [...hidden]]]];
  const active = regions === undefined ? null : regionFilter('line', regions);
  for (const { spec, filter } of LINE_LAYERS)
    map.setFilter(
      spec.id,
      active || shown ? ['all', filter, ...(active ? [active] : []), ...(shown ? [shown] : [])] : filter,
    );
}

// Dim every line but the focused one (hovering a line in the panel).
export function dimLines(map: MapLibreMap, focus: LineId | null): void {
  for (const layer of LINE_LAYERS) {
    if (layer.opacity === null) continue;
    map.setPaintProperty(layer.spec.id, 'line-opacity', lineOpacity(layer, layer.opacity, focus));
  }
}
